import { useState } from "react";
import axios from "axios";
import { FileDown, CheckCircle2, Loader2, BookOpen } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
const GUIDE_URL = "/marketing/fieldz-ai-pricing-guide.pdf";

const GuideCapture = ({ source = "" }) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Please enter a valid email address");
      return;
    }
    setError("");
    setStatus("loading");
    try {
      await axios.post(`${API}/marketing/guide-lead`, { email: email.trim(), source });
      setStatus("done");
    } catch {
      setStatus("idle");
      setError("Something went wrong — please try again.");
    }
  };

  return (
    <div
      className="relative overflow-hidden rounded-2xl bg-[#0F172A] p-7 sm:p-8 my-10 shadow-[0_20px_40px_-12px_rgba(15,23,42,0.4)]"
      data-testid="guide-capture"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{ background: "radial-gradient(ellipse 480px 240px at 15% 0%, rgba(0,70,190,0.4) 0%, transparent 70%)" }}
      />
      <div className="relative z-10">
        {status === "done" ? (
          <div className="text-center py-2" data-testid="guide-capture-success">
            <div className="w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-6 h-6 text-emerald-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">You're in — grab your guide</h3>
            <p className="text-[14px] text-slate-300 mb-5 max-w-md mx-auto">
              Your copy of the Field Service Software Pricing Guide is ready to download.
            </p>
            <a
              href={GUIDE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mk-btn mk-btn-white"
              data-testid="guide-download-btn"
            >
              <FileDown className="w-4 h-4" /> Download the Guide (PDF)
            </a>
          </div>
        ) : (
          <div className="grid md:grid-cols-[auto_1fr] gap-5 items-center">
            <div className="hidden md:flex w-14 h-14 rounded-2xl bg-white/[0.08] border border-white/10 items-center justify-center shrink-0">
              <BookOpen className="w-6 h-6 text-sky-400" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-sky-400 mb-1.5">Free download</p>
              <h3 className="text-xl font-bold text-white mb-1.5">Get the Field Service Pricing Guide</h3>
              <p className="text-[13.5px] text-slate-300 leading-relaxed mb-4">
                The 4 pricing models vendors use, a hidden-cost checklist, and a worksheet to calculate any platform's true monthly cost.
              </p>
              <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2.5" noValidate>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourcompany.com"
                  className="flex-1 rounded-full px-5 py-3 text-[14px] bg-white/[0.07] border border-white/15 text-white placeholder:text-slate-400 focus:outline-none focus:border-sky-400 transition-colors"
                  data-testid="guide-email-input"
                />
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="mk-btn mk-btn-primary shrink-0"
                  data-testid="guide-submit-btn"
                >
                  {status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                  Get the Guide
                </button>
              </form>
              {error && <p className="text-[12.5px] text-rose-400 mt-2" data-testid="guide-capture-error">{error}</p>}
              <p className="text-[11.5px] text-slate-500 mt-2.5">Instant download. No spam — unsubscribe anytime.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GuideCapture;
